/**
 * Talent Acquisition MVP — HM shared Vacancy Requests inbox.
 * Advisors: start recruitment process (with confirmation dialog) + close.
 * HM Admin: strictly read-only (founder Q1).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Paperclip, Play, XCircle, Download } from 'lucide-react';

const STATUS_TONE = {
  submitted: 'bg-info/10 text-info border-info/20',
  in_process: 'bg-success/10 text-success border-success/20',
  closed: 'text-muted-foreground',
};

export function VacancyInboxAdmin({ isAdvisor }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('submitted');
  const [detail, setDetail] = useState(null);
  const [confirmStart, setConfirmStart] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get('/admin/vacancy-requests', { params: status === 'all' ? {} : { status } }).then((r) => r.data));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const startProcess = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/vacancy-requests/${detail.id}/start-process`).then((r) => r.data);
      toast.success(t('vacancy.processStarted', { defaultValue: 'Proceso de reclutamiento iniciado.' }));
      navigate(`/admin/recruitment/${res.process_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmStart(false);
    } finally { setBusy(false); }
  };

  const closeRequest = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vacancy-requests/${detail.id}/close`, { reason: closeReason });
      toast.success(t('vacancy.closed', { defaultValue: 'Solicitud cerrada.' }));
      setCloseOpen(false); setDetail(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadJd = async (v) => {
    try {
      const res = await api.get(`/admin/vacancy-requests/${v.id}/jd-file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = v.jd_file?.filename || 'jd';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const F = ({ label, value }) => value == null || value === '' ? null : (
    <div>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="vacancy-inbox">
      <div className="flex items-center gap-2">
        {['submitted', 'in_process', 'closed', 'all'].map((s) => (
          <Button key={s} size="sm" variant={status === s ? 'default' : 'outline'} className="h-8 text-xs"
            onClick={() => setStatus(s)} data-testid={`vacancy-filter-${s}`}>
            {t(`vacancy.statuses.${s}`, { defaultValue: s === 'all' ? t('vacancy.all', { defaultValue: 'Todas' }) : s })}
            {s !== 'all' && data.counts?.[s] != null ? ` (${data.counts[s]})` : ''}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      ) : data.items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground shadow-card" data-testid="vacancy-inbox-empty">
          {t('vacancy.inboxEmpty', { defaultValue: 'No hay solicitudes en este estado.' })}
        </Card>
      ) : (
        <div className="space-y-3">
          {data.items.map((v) => (
            <Card key={v.id} className="p-4 shadow-card flex items-center gap-3 cursor-pointer hover:border-primary/30 transition-smooth"
              onClick={() => setDetail(v)} data-testid={`vacancy-inbox-row-${v.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm">{v.position_title} <span className="text-muted-foreground font-normal">· {v.company_name}</span></p>
                <p className="text-xs text-muted-foreground">
                  {v.department} · {v.openings} {t('vacancy.openingsShort', { defaultValue: 'plazas' })}
                  · {t(`vacancy.priorities.${v.hiring_priority}`, { defaultValue: v.hiring_priority })}
                  {v.desired_start_date ? ` · ${t('vacancy.start', { defaultValue: 'Inicio' })} ${v.desired_start_date}` : ''}
                </p>
              </div>
              {v.jd_file && <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              <Badge variant="outline" className={`text-xs ${STATUS_TONE[v.status] || ''}`}>
                {t(`vacancy.statuses.${v.status}`, { defaultValue: v.status })}
              </Badge>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail && !confirmStart && !closeOpen} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vacancy-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.position_title}
                  <Badge variant="outline" className={`text-xs ${STATUS_TONE[detail.status] || ''}`}>
                    {t(`vacancy.statuses.${detail.status}`, { defaultValue: detail.status })}
                  </Badge>
                </DialogTitle>
                <DialogDescription>{detail.company_name} · {detail.created_by_name}</DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-3 gap-3">
                <F label={t('vacancy.department', { defaultValue: 'Departamento' })} value={detail.department} />
                <F label={t('vacancy.openings', { defaultValue: 'Nº de plazas' })} value={detail.openings} />
                <F label={t('vacancy.employmentType', { defaultValue: 'Tipo de empleo' })} value={t(`vacancy.employmentTypes.${detail.employment_type}`, { defaultValue: detail.employment_type })} />
                <F label={t('vacancy.startDate', { defaultValue: 'Inicio deseado' })} value={detail.desired_start_date} />
                <F label={t('vacancy.priority', { defaultValue: 'Prioridad' })} value={t(`vacancy.priorities.${detail.hiring_priority}`, { defaultValue: detail.hiring_priority })} />
                <F label={t('vacancy.location', { defaultValue: 'Ubicación' })} value={detail.work_location} />
                <F label={t('vacancy.schedule', { defaultValue: 'Horario' })} value={detail.work_schedule && t(`vacancy.schedules.${detail.work_schedule}`, { defaultValue: detail.work_schedule })} />
                <F label={t('vacancy.gender', { defaultValue: 'Género preferido' })} value={detail.preferred_gender !== 'any' ? t(`vacancy.genders.${detail.preferred_gender}`, { defaultValue: detail.preferred_gender }) : null} />
                <F label={t('vacancy.ageRange', { defaultValue: 'Rango de edad' })} value={detail.age_range ? `${detail.age_range.min}–${detail.age_range.max}` : null} />
                <F label={t('vacancy.education', { defaultValue: 'Educación' })} value={detail.education_level && t(`vacancy.educationLevels.${detail.education_level}`, { defaultValue: detail.education_level })} />
                <F label={t('vacancy.experience', { defaultValue: 'Años de experiencia' })} value={detail.years_experience} />
                <F label={t('vacancy.budget', { defaultValue: 'Presupuesto' })} value={detail.budget_defined ? `${detail.budget_from} – ${detail.budget_to}` : null} />
              </div>
              {(detail.languages || []).length > 0 && (
                <F label={t('vacancy.languages', { defaultValue: 'Idiomas' })}
                  value={detail.languages.map((l) => `${l.language} (${t(`vacancy.proficiencies.${l.proficiency}`, { defaultValue: l.proficiency })})`).join(', ')} />
              )}
              <F label={t('vacancy.technical', { defaultValue: 'Conocimientos técnicos' })} value={detail.technical_knowledge} />
              <F label={t('vacancy.competencies', { defaultValue: 'Competencias descritas' })} value={detail.competencies_text} />
              <F label={t('vacancy.comments', { defaultValue: 'Comentarios' })} value={detail.comments} />
              {detail.status === 'closed' && <F label={t('vacancy.closeReason', { defaultValue: 'Motivo de cierre' })} value={detail.closed_reason} />}
              {detail.jd_file && (
                <Button size="sm" variant="outline" className="gap-1.5 w-fit" onClick={() => downloadJd(detail)} data-testid="vacancy-jd-download">
                  <Download className="h-3.5 w-3.5" /> {detail.jd_file.filename}
                </Button>
              )}
              <DialogFooter className="gap-2">
                {isAdvisor && detail.status === 'submitted' ? (
                  <>
                    <Button variant="outline" className="gap-1.5" onClick={() => { setCloseReason(''); setCloseOpen(true); }} data-testid="vacancy-close-btn">
                      <XCircle className="h-3.5 w-3.5" /> {t('vacancy.close', { defaultValue: 'Cerrar solicitud' })}
                    </Button>
                    <Button className="gap-1.5" onClick={() => setConfirmStart(true)} data-testid="vacancy-start-btn">
                      <Play className="h-3.5 w-3.5" /> {t('vacancy.startProcess', { defaultValue: 'Iniciar proceso de reclutamiento' })}
                    </Button>
                  </>
                ) : !isAdvisor && detail.status === 'submitted' ? (
                  <p className="text-xs text-muted-foreground" data-testid="vacancy-admin-readonly-note">
                    {t('vacancy.adminReadonly', { defaultValue: 'Solo los Advisors pueden iniciar o cerrar solicitudes.' })}
                  </p>
                ) : detail.process_id && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/admin/recruitment/${detail.process_id}`)} data-testid="vacancy-open-process">
                    {t('vacancy.openProcess', { defaultValue: 'Ver proceso' })}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Start confirmation (founder adjustment #4) */}
      <Dialog open={confirmStart} onOpenChange={setConfirmStart}>
        <DialogContent data-testid="vacancy-start-confirm">
          <DialogHeader>
            <DialogTitle>{t('vacancy.startProcess', { defaultValue: 'Iniciar proceso de reclutamiento' })}</DialogTitle>
          </DialogHeader>
          <ul className="text-sm space-y-2 list-disc pl-5 text-muted-foreground">
            <li>{t('vacancy.startNote1', { defaultValue: 'La solicitud del cliente pasará a solo lectura.' })}</li>
            <li>{t('vacancy.startNote2', { defaultValue: 'Se creará un Proceso de Reclutamiento TalentCheck.' })}</li>
            <li>{t('vacancy.startNote3', { defaultValue: 'La información de la vacante se copiará al proceso.' })}</li>
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmStart(false)} data-testid="vacancy-start-cancel">
              {t('common.cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button onClick={startProcess} disabled={busy} data-testid="vacancy-start-confirm-btn">
              {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('vacancy.startConfirm', { defaultValue: 'Confirmar e iniciar' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent data-testid="vacancy-close-dialog">
          <DialogHeader>
            <DialogTitle>{t('vacancy.close', { defaultValue: 'Cerrar solicitud' })}</DialogTitle>
            <DialogDescription>{t('vacancy.closeHint', { defaultValue: 'Indica brevemente el motivo.' })}</DialogDescription>
          </DialogHeader>
          <Textarea rows={2} value={closeReason} onChange={(e) => setCloseReason(e.target.value)} data-testid="vacancy-close-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
            <Button variant="destructive" disabled={busy || closeReason.trim().length < 3} onClick={closeRequest} data-testid="vacancy-close-confirm">
              {t('vacancy.closeConfirm', { defaultValue: 'Cerrar solicitud' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VacancyInboxAdmin;
